/** Per-agent reports and KPI labels (M29). Shell created by WP-M29-SCHEMA1: later work packages add keys inside their group (en and fil together, no dots inside key segments). */
export const agentReports = {
  tickets: {
    title: "Agent report",
    sub: "Tickets per agent, {{period}}. Tickets nobody holds are not counted.",
    caption: "Ticket figures by agent",
    teamFilter: "Team",
    allTeams: "All teams",
    export: "Export CSV",
    exportDone: "{{file}} saved.",
    exportFailed: "We could not export the agent report. Please try again.",
    total: "All agents",
    seconds: "{{value}} s",
    openLink: "Open the agent report",
    columns: {
      agent: "Agent",
      volume: "Tickets",
      resolved: "Resolved",
      firstResponse: "First response",
      resolution: "Resolution",
      slaMet: "SLA met",
      csat: "CSAT",
      reopens: "Reopens",
    },
    empty: {
      title: "No agent figures in this period",
      body: "Figures appear here once tickets are assigned to people. Assign tickets to your team, or pick a longer period.",
      action: "Open tickets",
      teamTitle: "No one in this team has figures in this period",
      teamBody: "Nobody in the chosen team had tickets assigned in this period. Choose another team or show everyone.",
      teamAction: "Show all teams",
    },
  },
  chats: {
    title: "Chat report",
    sub: "Live chats per agent, {{period}}. Each figure is counted on the day it happened.",
    caption: "Chat figures by agent",
    viewLabel: "Rows",
    views: { member: "By agent", day: "By day" },
    teamFilter: "Team",
    allTeams: "All teams",
    export: "Export CSV",
    exportDone: "{{file}} saved.",
    exportFailed: "We could not export the chat report. Please try again.",
    total: "All agents",
    asOf: "Figures as of {{time}}. Refreshed every 15 minutes.",
    error: "We could not load the chat report.",
    concurrencyValue: "{{avg}} (peak {{peak}})",
    columns: {
      agent: "Agent",
      day: "Day",
      chats: "Chats",
      firstResponse: "Staff first response",
      handleTime: "Handle time",
      concurrency: "Chats at once",
      transfers: "Transfers",
      fcr: "First-contact resolution",
      csat: "CSAT",
      aiAssisted: "AI-assisted replies",
    },
    definitionsTitle: "How these figures are counted",
    definitions: {
      chats: "Chats a person accepted and answered: taken over, accepted from an offer, assigned to them, or joined by replying, with at least one reply from them. Counted on the day of the accept, once per person and chat.",
      firstResponse: "Average time from the moment a chat was assigned to or accepted by the person to their first reply in it. A chat they joined by replying first has no waiting time and is left out.",
      handleTime: "Average time from the accept to the close, for the answered chats the person was holding when they were resolved or closed. Counted on the day of the close.",
      concurrency: "Average number of chats the person held at once while holding at least one, and the most held at the same moment. A chat is held from the accept until it is closed, handed on, or quiet for 30 minutes.",
      transfers: "Answered chats the person handed on: reassigned to someone else or returned to the queue.",
      fcr: "Share of the person's answered chats, closed by them, with no reopening and no new chat from the same customer on the same topic within 72 hours. A chat counts once its 72 hours have passed.",
      csat: "Average rating (1 to 5) customers gave to the chats the person held last. Counted on the day of the rating.",
      aiAssisted: "Share of the person's replies that came after an AI draft they asked for in the same chat since their previous reply.",
    },
    empty: {
      title: "No chat figures in this period",
      body: "Figures appear here once people take and answer live chats. The report refreshes every 15 minutes, so a chat answered a moment ago may not show yet.",
      action: "Open the inbox",
      teamTitle: "No one in this team has chat figures in this period",
      teamBody: "Nobody in the chosen team answered a chat in this period. Choose another team or show everyone.",
      teamAction: "Show all teams",
    },
  },
  kpi: {
    aiAnswerTime: "AI answer time",
    staffFirstResponse: "Staff first response",
    aiAnswerNote: "How fast the assistant replies",
    staffNote: "Handover to the first staff message",
    staffNone: "No handover answered by staff yet",
    p95: "p95 {{value}}",
    ms: "{{value}} ms",
    seconds: "{{value}} s",
    minutes: "{{value}} min",
    hours: "{{value}} h",
  },
  definitions: {
    aiAnswerTime: "How long the assistant takes to reply to a message. It says nothing about how quickly people answer.",
    staffFirstResponse: "Time from the moment a chat is handed to staff until the first message a person sends in it. Handovers nobody has answered yet are not counted.",
  },
  schedules: {
    open: "Scheduled exports",
    title: "Scheduled exports",
    sub: "E-mail a link to a per-agent CSV every day or week. The link works for 3 days; the file itself is never attached.",
    error: "We could not load your scheduled exports.",
    reports: { agent_tickets: "Agent report (tickets)", agent_chats: "Chat report (live chats)" },
    cadence: { daily: "Every day", weekly: "Every week" },
    empty: {
      title: "No scheduled exports yet",
      body: "Choose a report, a time and who should get it. They receive a link by e-mail that stops working after 3 days.",
      unavailable: "None of the per-agent reports is available to you in this workspace, so there is nothing to schedule.",
      action: "New schedule",
    },
    list: {
      label: "Scheduled exports",
      daily: "Every day at {{time}} ({{zone}})",
      weekly: "Every {{day}} at {{time}} ({{zone}})",
      recipients: "Recipients ({{count}}): {{list}}",
      next: "Next e-mail {{time}}",
      noNext: "Paused: no e-mail will be sent",
      last: "last sent {{time}}",
      count: "{{count}} of {{max}} schedules",
      withReferences: "With customer references",
      pausedBadge: "Paused",
      enabledLabel: "Send {{name}}",
      edit: "Edit {{name}}",
      editShort: "Edit",
      delete: "Delete {{name}}",
      paused: "{{name}} paused.",
      resumed: "{{name}} will be sent again.",
    },
    form: {
      newTitle: "New schedule",
      editTitle: "Edit schedule",
      name: "Name",
      namePlaceholder: "Monday agent report",
      nameRequired: "Give the schedule a name.",
      report: "Report",
      reportLocked: "The report cannot be changed. Create a new schedule for another report.",
      cadence: "How often",
      weekday: "Day of the week",
      hour: "Time",
      timezone: "Time zone",
      recipients: "Recipients",
      recipientsHint: "E-mail addresses separated by commas or new lines, at most {{count}}.",
      recipientsPlaceholder: "name@example.com",
      recipients_empty: "Add at least one e-mail address.",
      recipients_invalid: "One of the addresses is not a valid e-mail address.",
      recipients_tooMany: "A schedule has at most {{count}} recipients.",
      references: "Include customer references",
      referencesHint: "Adds a column with the references of the customers each agent answered. Anyone with the link can read them until it expires.",
      referencesLocked: "Only people who may view customer references can switch this on.",
      create: "Create schedule",
      save: "Save changes",
      created: "Schedule created.",
      saved: "Schedule saved.",
    },
    remove: {
      title: "Delete this schedule?",
      body: "{{name}} stops now, and links it already sent stop working.",
      confirm: "Delete schedule",
      done: "Schedule deleted.",
    },
  },
} as const;
